import React, { useMemo } from 'react';
import { Tooltip, Avatar } from 'antd';
import { Cloud } from 'lucide-react';

import DataTable from '../../../../../../../shared/designSystem/components/DataTable';
import { TransactionCreativeExpandedCard } from './TransactionCreativeExpandedCard';
import { formatWholeNumber } from '../../../../../../../../core/utils/campaigns';

// =============================================================================
// TransactionCreativeTable (V1) — migrated to the shared DataTable primitive.
// Column model preserved: color swatch · optional thumbnail · Name ·
// Impressions · Transactions. Conditional `creativeUrl` thumbnail column
// only mounts when at least one row has a url — same behavior as before.
//
// Expanded row preserved: <TransactionCreativeExpandedCard/> renders for
// rows with `creativeUrl` (or THREEDEE media). DataTable's `expandable.
// isRowExpandable` replaces V1's `rowClassName="hide-expand"` trick.
// =============================================================================

const sortString = (key) => (a, b) =>
  String(a.original[key] || '').localeCompare(String(b.original[key] || ''));
const sortNumber = (key) => (a, b) =>
  (Number(a.original[key]) || 0) - (Number(b.original[key]) || 0);

const TransactionCreativeTable = ({ creativeData = [] }) => {
  const hasThumbnails = useMemo(
    () => creativeData.some((creative) => creative && creative.creativeUrl),
    [creativeData]
  );

  const columns = useMemo(() => {
    const color = {
      id: 'color',
      accessorKey: 'color',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 32 },
      cell: ({ getValue }) => (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: 9999,
            background: getValue() || '#cbd5e1',
          }}
        />
      ),
    };

    const thumb = {
      id: 'creativeUrl',
      accessorKey: 'creativeUrl',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 68 },
      cell: ({ getValue }) => {
        const url = getValue();
        return (
          <Avatar
            shape="square"
            size={44}
            src={url || undefined}
            icon={!url ? <Cloud size={18} strokeWidth={2} /> : undefined}
          />
        );
      },
    };

    const name = {
      id: 'value',
      accessorKey: 'value',
      header: 'Name',
      sortable: true,
      sortingFn: sortString('value'),
      meta: { align: 'left' },
      cell: ({ getValue }) => {
        const text = getValue();
        if (!text) return null;
        if (String(text).length > 45) {
          return (
            <Tooltip overlayClassName="mjl-ds" title={text}>
              <span>{String(text).slice(0, 46)}…</span>
            </Tooltip>
          );
        }
        return <span>{text}</span>;
      },
    };

    const impressions = {
      id: 'impressions',
      accessorKey: 'impressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: sortNumber('impressions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    };

    const transactions = {
      id: 'totalTransactions',
      accessorKey: 'totalTransactions',
      header: 'Transactions',
      sortable: true,
      sortingFn: sortNumber('totalTransactions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? formatWholeNumber(v) : 0}</span>;
      },
    };

    return hasThumbnails ? [color, thumb, name, impressions, transactions] : [color, name, impressions, transactions];
  }, [hasThumbnails]);

  const rows = useMemo(
    () => creativeData.map((c, i) => ({ ...c, __key: i })),
    [creativeData]
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      pageSizeOptions={[10, 20, 50, 100]}
      expandable={{
        isRowExpandable: (record) =>
          Boolean(record.creativeUrl) ||
          (record.media && record.media.type) === 'THREEDEE',
        renderExpandedRow: (record) => <TransactionCreativeExpandedCard record={record} />,
      }}
      emptyState="No creatives yet."
    />
  );
};

export default TransactionCreativeTable;
